import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, Package, DollarSign, Clock, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast, Toaster } from '../components/ui/sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyBookings = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const searchBookings = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/booking/my-bookings/${encodeURIComponent(email)}`);
      setBookings(response.data);
      setSearched(true);
      if (response.data.length === 0) {
        toast.info('No bookings found for this email');
      }
    } catch (error) {
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'pending_payment': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <Toaster />
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">My Bookings</h1>
          <p className="text-gray-600 mt-2">View your past and upcoming junk removal bookings</p>
        </div>

        {/* Search Form */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <form onSubmit={searchBookings} className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full"
                  data-testid="booking-search-email"
                />
              </div>
              <Button 
                type="submit" 
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={loading}
                data-testid="booking-search-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? 'Searching...' : 'Find My Bookings'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {searched && (
          <div className="space-y-4" data-testid="booking-results">
            {bookings.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="mx-auto bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                    <Calendar className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Bookings Found</h3>
                  <p className="text-gray-600 mb-4">We couldn't find any bookings associated with this email.</p>
                  <Button onClick={() => navigate('/#pricing')} className="bg-emerald-600 hover:bg-emerald-700">
                    Book a Service
                  </Button>
                </CardContent>
              </Card>
            ) : (
              bookings.map((booking) => (
                <Card key={booking.booking_id} className="overflow-hidden" data-testid={`booking-card-${booking.booking_id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1">
                        {/* Status Badges */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(booking.booking_status)}`}>
                            {booking.booking_status === 'confirmed' && <CheckCircle className="w-4 h-4 inline mr-1" />}
                            {booking.booking_status.replace('_', ' ').toUpperCase()}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPaymentStatusColor(booking.payment_status)}`}>
                            {booking.payment_status === 'paid' ? '✓ PAID' : 'Payment ' + booking.payment_status}
                          </span>
                        </div>

                        {/* Package Info */}
                        <div className="flex items-center space-x-3 mb-2">
                          <Package className="w-5 h-5 text-emerald-600" />
                          <span className="text-lg font-semibold text-gray-900">{booking.package_name}</span>
                        </div>

                        {/* Date & Time */}
                        <div className="flex items-center space-x-3 mb-2">
                          <Calendar className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-700">{formatDate(booking.service_date)}</span>
                          {booking.service_time && (
                            <>
                              <Clock className="w-5 h-5 text-gray-400 ml-4" />
                              <span className="text-gray-700 capitalize">{booking.service_time}</span>
                            </>
                          )}
                        </div>

                        {/* Address */}
                        <p className="text-gray-600 text-sm mt-2">{booking.address}</p>

                        {/* Notes */}
                        {booking.notes && (
                          <p className="text-gray-500 text-sm mt-2 italic">"{booking.notes}"</p>
                        )}

                        {/* Booking ID */}
                        <p className="text-xs text-gray-400 mt-3">
                          Booking ID: {booking.booking_id}
                        </p>
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <DollarSign className="w-6 h-6 text-emerald-600" />
                          <span className="text-3xl font-bold text-emerald-600">{booking.price}</span>
                        </div>
                        <p className="text-sm text-gray-500">CAD</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Help Section */}
        <Card className="mt-8 bg-emerald-50 border-emerald-200">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-emerald-800 mb-2">Need Help?</h3>
            <p className="text-emerald-700 mb-4">
              If you have questions about your booking or need to make changes, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:3653363364">
                <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-100">
                  Call (365) 336-3364
                </Button>
              </a>
              <a href="mailto:Info.junkio@gmail.com">
                <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-100">
                  Email Us
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default MyBookings;
